/*!
 * Dr.Golf Swing Test v2 (golf-quiz.js)
 * UI, routing, state and PDP hand-off for /pages/swing-test.
 * Reuses the shared v1 engine unchanged: DrGolf.config, DrGolf.content, DrGolf.engine, DrGolf.analytics.
 * Never reads the v1 offer config: every credit value comes from the section settings (#gq-data).
 * Browser: DrGolf.quiz2 (auto-boots on #gq-app). Node: module.exports = pure helpers (tests).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module && module.exports) { module.exports = api; return; }
  root.DrGolf = root.DrGolf || {};
  root.DrGolf.quiz2 = api;
  if (root.document) {
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', function () { api.boot(root); });
    else api.boot(root);
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ================= PURE HELPERS (exported for tests) ================= */

  var DEFAULT_ORDER = ['q1', 'q2', 'q4', 'q8', 'q6', 'q7', 'q3', 'q5', 'q9', 'q10'];
  var ANGLE_Q = { range: 'q4', power: 'q7', pressure: 'q10', consistency: 'q1', 'default': 'q1' };
  var HOOKS = {
    range: 'WHY YOUR RANGE SWING DISAPPEARS ON THE COURSE',
    power: 'WHY SWINGING HARDER COSTS YOU CONTACT',
    pressure: 'WHAT HAPPENS TO YOUR SWING WHEN THE SHOT MATTERS',
    consistency: 'GREAT SHOT, BAD SHOT, GREAT SHOT. WHY?'
  };
  var DEFAULT_HOOK = "WHAT'S REALLY BREAKING DOWN IN YOUR SWING?";
  var HANDOFF_KEY = 'drgolf.assessment.v1';
  /* Credit terms on their own key, written on every purchase CTA, so the product page can show the credit even when
     the diagnosis hand-off is withheld (bundle from a single-priority result). Read by golf-pdp.js quizCredit(). */
  var CREDIT_KEY = 'drgolf.credit.v1';
  var FALLBACK_URL = { tempo: '/products/tempo-swing-stick', band: '/products/arm-connection-band', bundle: '/products/dr-golf-bundle' };
  var FALLBACK_NAME = { tempo: 'Tempo Swing Stick', band: 'Connection Band', bundle: 'Dr.Golf Bundle' };

  function L(icon, label, detail) { return { icon: icon, label: label, detail: detail || '' }; }
  /* Display copy for the cards (the words the golfer taps). Hand-off and evidence always use config text. */
  var LABELS = {
    q1: { A: L('scatter', 'Hit-or-miss contact', 'Pure one swing, fat or thin the next'), B: L('fast', 'I get quick', 'Rushed, and my rhythm’s gone'), C: L('arms', 'My arms take over', 'Hands and arms instead of my body'), D: L('repeat', 'Great shots I can’t repeat', 'The good ones don’t come back'), E: L('bolt', 'Going for distance wrecks it', 'Everything gets worse when I add speed') },
    q2: { A: L('strike-fatthin', 'Fat and thin', 'Both show up'), B: L('strike-heeltoe', 'Heel or toe', 'Off the middle more than I’d like'), C: L('strike-wander', 'Solid, but it wanders', 'Direction and distance vary'), D: L('metronome', 'Strike’s fine, timing isn’t', 'The swing feels mistimed'), E: L('strike-effort', 'Worse when I swing harder', 'Contact drops off with effort') },
    q3: { A: L('arc', 'Smooth and controlled', 'My problems happen later'), B: L('fast', 'I get quick from the top', 'I start down aggressively'), C: L('arms', 'My arms fire first', 'Before the rest of my body'), D: L('body-ahead', 'Body turns, arms get left behind', 'The arms feel late'), E: L('shuffle', 'It depends on the shot', 'Changes with the shot or the pressure') },
    q4: { A: L('equals', 'Basically the same', 'Pressure doesn’t change much'), B: L('fast', 'A bit quicker', 'Once the score matters'), C: L('grip', 'Tighter hands and arms', 'Noticeably tense on the course'), D: L('steer', 'I start steering it', 'Guiding instead of swinging free'), E: L('parking', 'Range swing stays in the parking lot', 'Some days it never shows up') },
    q5: { A: L('sync', 'In sync', 'They generally feel synchronized'), B: L('drift', 'My arms drift off my chest', 'They feel independent at times'), C: L('stall', 'Body stops, arms keep going', 'Hands and arms carry on alone'), D: L('body-ahead', 'Body races, arms catch up', 'I rotate hard and my arms lag'), E: L('question', 'Honestly, not sure', 'It won’t count for or against you') },
    q6: { A: L('feather', 'Smooth and almost effortless'), B: L('sync', 'Arms and torso moving together'), C: L('gauge', 'Surprisingly easy', 'I’m not manufacturing speed'), D: L('balance', 'Balanced from start to finish'), E: L('quiet', 'I stopped thinking and just swung') },
    q7: { A: L('equals', 'Not much', 'My motion stays the same'), B: L('fast', 'My transition speeds up', 'Noticeably faster from the top'), C: L('arms', 'Hands and arms take over', 'They start dominating the swing'), D: L('wobble', 'Balance or shape falls apart'), E: L('bolt', 'Contact goes before distance comes') },
    q9: { A: L('target', 'Target, setup, swing', 'Pretty simple'), B: L('thought', 'One or two swing thoughts', 'Mechanical, but manageable'), C: L('checklist', 'A checklist of positions', 'Several things I’m trying to control'), D: L('shuffle', 'Depends on my last bad shot', 'The thought keeps changing'), E: L('crowd', 'Too many fixes competing', 'I’ve watched a lot of golf instruction') },
    q10: { A: L('equals', 'Very little', 'I make my normal swing'), B: L('fast', 'I get quicker', 'Backswing or transition speeds up'), C: L('grip', 'Grip and arms tighten', 'Noticeably tighter'), D: L('steer', 'I steer it through impact', 'Guiding the club'), E: L('bolt', 'I add effort', 'To make sure I get through it') }
  };

  /* 24x24 line icons, stroke currentColor 1.75 */
  var FACE = '<rect x="3" y="7" width="18" height="10" rx="4"/>';
  var ICONS = {
    scatter: '<circle cx="6" cy="7" r="1.4"/><circle cx="17" cy="5" r="1.4"/><circle cx="12" cy="13" r="1.4"/><circle cx="5" cy="18" r="1.4"/><circle cx="18" cy="17" r="1.4"/>',
    fast: '<path d="M3 8h6M2 12h8M3 16h6M13 6l6 6-6 6"/>',
    arms: '<circle cx="12" cy="5" r="2"/><path d="M12 7v7M12 9.5 5.5 13M12 9.5l6.5 3.5M9 21l3-7 3 7"/>',
    repeat: '<path d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4"/>',
    bolt: '<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>',
    'strike-fatthin': FACE + '<circle cx="12" cy="9.6" r=".9"/><circle cx="12" cy="14.4" r=".9"/>',
    'strike-heeltoe': FACE + '<circle cx="6.8" cy="12" r=".9"/><circle cx="17.2" cy="12" r=".9"/>',
    'strike-wander': FACE + '<circle cx="12" cy="12" r=".9"/><path d="M9 5 6.5 2M15 5l2.5-3"/>',
    'strike-effort': FACE + '<circle cx="8" cy="14.2" r=".9"/><circle cx="16" cy="9.8" r=".9"/><path d="M20 1.5 18.5 4h2L19 6.5"/>',
    metronome: '<path d="M7 21h10L14 3h-4L7 21zM12 16l5-10"/>',
    arc: '<path d="M4 18a10 10 0 0 1 16 0"/><circle cx="20" cy="18" r="1.4"/>',
    'body-ahead': '<path d="M5 12a7 7 0 0 1 12-5M17 3v4h-4M4 18h6"/>',
    shuffle: '<path d="M3 7h4l10 10h4M3 17h4l3-3M14 10l3-3h4M18 4l3 3-3 3M18 14l3 3-3 3"/>',
    equals: '<path d="M5 9h14M5 15h14"/>',
    grip: '<path d="M7 11V7a2 2 0 0 1 4 0v4M11 10V6a2 2 0 0 1 4 0v5M15 10a2 2 0 0 1 4 0v3a7 7 0 0 1-7 7h-1a6 6 0 0 1-6-6v-2a2 2 0 0 1 2-2"/>',
    steer: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2"/><path d="M3 12h7M14 12h7M12 14v7"/>',
    parking: '<rect x="4" y="3" width="16" height="18" rx="3"/><path d="M10 17V8h3a2.5 2.5 0 0 1 0 5h-3"/>',
    sync: '<path d="M4 8h14l-3-3M4 16h14l-3 3"/>',
    drift: '<path d="M12 4v16M12 8 6 10M12 8l7 5"/>',
    stall: '<path d="M6 5v14M10 12h10M16 8l4 4-4 4"/>',
    question: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14M12 17.5v.01"/>',
    feather: '<path d="M20 4C11 4 6 9 6 18M6 18l-2 2M6 14h6M9 10h7"/>',
    gauge: '<path d="M4 17a8 8 0 1 1 16 0M12 17l4-5"/>',
    balance: '<path d="M12 4v16M6 20h12M5 8h14M5 8l-2 5h4zM19 8l-2 5h4z"/>',
    quiet: '<circle cx="12" cy="12" r="9"/><path d="M8 12h8"/>',
    wobble: '<path d="M3 12c3-6 5 6 9 0s6 6 9 0"/>',
    target: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r=".6"/>',
    thought: '<path d="M5 5h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-8l-4 4v-4H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"/>',
    checklist: '<path d="M4 6l1.5 1.5L8 5M4 12l1.5 1.5L8 11M4 18l1.5 1.5L8 17M11 6h9M11 12h9M11 18h9"/>',
    crowd: '<path d="M3 5h10v6H7l-3 3v-3H3zM11 13h10v6h-1v3l-3-3h-6z"/>',
    check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
    back: '<path d="M19 12H5M11 18l-6-6 6-6"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.01"/>',
    close: '<path d="M6 6l12 12M18 6 6 18"/>'
  };

  function findQ(cfg, qid) { for (var i = 0; i < cfg.questions.length; i++) if (cfg.questions[i].id === qid) return cfg.questions[i]; return null; }
  function findOpt(q, oid) { if (!q) return null; for (var i = 0; i < q.options.length; i++) if (q.options[i].id === oid) return q.options[i]; return null; }
  function w(o, d) { return (o && o.w && o.w[d]) || 0; }
  function capFirst(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }
  function isHigh(lv) { return lv === 'moderate' || lv === 'high'; }

  function normAngle(a) {
    a = String(a || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(ANGLE_Q, a) ? a : 'default';
  }
  function buildOrder(angle) {
    var q = ANGLE_Q[angle] || 'q1';
    if (q === 'q1') return DEFAULT_ORDER.slice();
    return [q].concat(DEFAULT_ORDER.filter(function (x) { return x !== q; }));
  }
  function validOrder(o) {
    return Array.isArray(o) && o.length === DEFAULT_ORDER.length && DEFAULT_ORDER.every(function (id) { return o.indexOf(id) !== -1; });
  }

  /** Display label for an option; falls back to the config label. */
  function labelFor(cfg, qid, oid) {
    var e = LABELS[qid] && LABELS[qid][oid];
    if (e) return { label: e.label, detail: e.detail, icon: e.icon };
    var o = findOpt(findQ(cfg, qid), oid);
    return { label: o ? o.label : '', detail: '', icon: null };
  }

  /** Tempo / connection signal counts over the given question ids. */
  function tally(cfg, answers, ids) {
    var min = cfg.engine.supportMinWeight, t = 0, c = 0;
    (ids || []).forEach(function (qid) {
      var o = findOpt(findQ(cfg, qid), answers[qid]);
      if (!o) return;
      if (w(o, 'T') >= min) t++;
      if (w(o, 'C') >= min) c++;
    });
    return { t: t, c: c };
  }
  function leanOf(t, c) {
    if (t > c && t >= 2) return 'tempo';
    if (c > t && c >= 2) return 'connection';
    if (t === c && t >= 2) return 'split';
    return 'none';
  }

  function firstSentence(s) { var i = s.indexOf('. '); return i === -1 ? s : s.slice(0, i + 1); }

  /** Coach note shown on display position `pos`, derived from the answer at pos-1. */
  function noteFor(cfg, order, pos, answers, opts) {
    var showHalfway = !(opts && opts.showHalfway === false);
    if (pos < 2 || pos > order.length) return null;
    var prev = order[pos - 2], next = order[pos - 1], ans = answers[prev], R = cfg.reactions || {};
    if (!ans) return null;
    var t = null;
    if (prev === 'q3' && R.q3) {
      t = R.q3[ans] || R.q3['*'];
      if (t && next !== 'q5') t = firstSentence(t);
    } else if (prev === 'q5' && R.q5) {
      t = ans === 'E' ? R.q5.E : null;
    } else if (prev === 'q6' && R.q6) {
      if (showHalfway && pos - 1 === 6) return null; // next screen was the halfway check
      var o = findOpt(findQ(cfg, 'q6'), ans);
      t = o && o.echo ? (R.q6[ans] || R.q6['*']).replace('{echo}', capFirst(o.echo)) : null;
    } else if (prev === 'q8' && R.q8) {
      t = R.q8[ans] || R.q8['*'];
    }
    return t ? { question: prev, text: t } : null;
  }

  /* ---- money: the visitor's currency, en-US style with the narrow symbol ("$69.99"), never a "CAD"/"USD" code ---- */
  function moneyFormat(currency, frac) {
    var cur = String(currency || '').toUpperCase();
    if (cur) {
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol', minimumFractionDigits: frac, maximumFractionDigits: 2 }); }
      catch (e) { /* older engines: plain $ below */ }
    }
    return { format: function (n) { return '$' + n.toFixed(frac); } };
  }
  /** Cents in the visitor's currency: 6999 -> "$69.99". */
  function formatMoney(cents, currency) { return moneyFormat(currency, 2).format(cents / 100); }
  /** Whole-unit amounts (credit, minimum, savings): 10 -> "$10", 10.5 -> "$10.50". */
  function formatWhole(d, currency) { return moneyFormat(currency, d % 1 ? 2 : 0).format(d).replace(/ /g, '\u00a0'); }

  /* ---- credit (amount and minimum in whole units of credit.currency) ---- */
  /** The visitor's credit from #gq-data. Each market has its own code set in that market's currency
      (GOLF10US in USD for the United States, GOLF10 in CAD for Canada), so the amounts shown are exact.
      A blank code, an amount that isn't above 0, or a visitor shopping in a currency other than the code's switches
      the credit off: no credit copy, plain product links, creditCode null in the hand-off. */
  function creditFor(data) {
    var cr = (data && data.credit) || {}, cur = String((data && data.currency) || '').toUpperCase();
    var code = String(cr.code || '').trim(), amt = +cr.amount, min = +cr.minimum, ccy = String(cr.currency || '').toUpperCase();
    if (!code || !(amt > 0) || (ccy && cur && ccy !== cur)) return { code: null, amount: 0, minimum: 0, currency: null };
    return { code: code, amount: amt, minimum: min > 0 ? min : 0, currency: ccy || cur || null };
  }
  /* ---- credit maths (cents; credit = {amount, minimum, currency} in whole units) ---- */
  /** currency: the visitor's ISO currency; the credit only applies to prices in its own currency. */
  function creditApplies(cents, credit, currency) {
    if (currency === false || typeof cents !== 'number' || !credit || !(credit.amount > 0)) return false;
    if (currency && credit.currency && String(currency).toUpperCase() !== String(credit.currency).toUpperCase()) return false;
    return cents >= Math.round(credit.minimum * 100);
  }
  function withCredit(cents, credit) { return cents - Math.round(credit.amount * 100); }
  /** Pre-credit saving vs buying both separately; null (hidden) unless > 0. */
  function separateSaving(tempo, band, bundle) {
    if ([tempo, band, bundle].some(function (x) { return typeof x !== 'number'; })) return null;
    var s = tempo + band - bundle;
    return s > 0 ? s : null;
  }
  function firstAvail(vs) { vs = vs || []; return vs.filter(function (v) { return v.available; })[0] || vs[0] || null; }
  /** Bundle variant whose option1/2/3 equals the band colour (case-insensitive), else first available. */
  function bundleVariantFor(bundle, colour) {
    if (!bundle || !bundle.variants || !bundle.variants.length) return null;
    var c = String(colour || '').trim().toLowerCase();
    var m = c ? bundle.variants.filter(function (v) {
      return [v.option1, v.option2, v.option3].some(function (o) { return o != null && String(o).trim().toLowerCase() === c; });
    })[0] : null;
    return m || firstAvail(bundle.variants);
  }
  function discountHref(code, url, variantId) {
    var target = url + (variantId != null && variantId !== '' ? (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variantId : '');
    return code ? '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(target) : target;
  }

  /** Purchase CTAs a result page offers, in order. */
  function ctaTargets(result) {
    var p = result.products || [];
    if (p.length > 1) return [{ product: 'bundle', location: 'card' }, { product: 'tempo', location: 'alt' }, { product: 'band', location: 'alt' }];
    if (p[0] === 'band') return [{ product: 'bundle', location: 'choice' }, { product: 'band', location: 'choice' }];
    var t = [{ product: 'tempo', location: 'card' }];
    if (result.levels && isHigh(result.levels.C)) t.push({ product: 'bundle', location: 'upsell' });
    return t;
  }

  /** v1 whyText, verbatim (cfg.products names). */
  function whyText(cfg, result, key) {
    var name = cfg.products[key].name;
    if (result.mode === 'combined') {
      return key === 'tempo'
        ? 'For the rhythm side of your pattern. The ' + name + ' gives you an audible cue for rehearsing a repeatable pace.'
        : 'For the connection side of your pattern. The ' + name + ' gives you a physical cue for keeping your arms moving with your body.';
    }
    if (result.mode === 'maintenance') {
      return key === 'tempo'
        ? 'To keep your rhythm familiar. The ' + name + ' turns a short warm-up into something you can hear, so the swing you bring to the first tee is the one you practiced.'
        : 'To keep your arms and body moving together. The ' + name + ' gives your warm-up a physical cue you can feel.';
    }
    var lead = result.mode === 'starter' ? 'As a starting focus, ' : 'Based on your answers, ';
    return key === 'tempo'
      ? lead + 'a repeatable rhythm is a useful place to begin training. The ' + name + ' gives you an audible cue for rehearsing that rhythm consistently.'
      : lead + 'arm–body connection is a useful place to begin training. The ' + name + ' gives you a physical cue for rehearsing that feeling, rep after rep.';
  }

  /** The chosen option with the highest weight on `dim` (>= minW), earliest in `order` on ties.
      q6 (the golfer's best swing) is a reference point, not a fault, so it is never quoted as a "flag". */
  function strongestAnswer(cfg, answers, dim, order, minW) {
    var best = null, bw = (minW || 2) - 1;
    (order || DEFAULT_ORDER).forEach(function (qid) {
      if (qid === 'q6') return;
      var o = findOpt(findQ(cfg, qid), answers[qid]);
      if (o && w(o, dim) > bw) { bw = w(o, dim); best = { question: qid, option: o.id }; }
    });
    return best;
  }

  /** One answer that points against the shown focus. Engine value for single/combined; for starter results the
      engine does not compute it, so read the first contradicting answer for the shown family. Maintenance: none
      (its stability answers are the evidence itself). */
  function counterFor(cfg, res) {
    if (res.counterEvidence) return res.counterEvidence;
    if (res.mode !== 'starter' || !res.support) return null;
    var s = res.support[res.family === 'tempo' ? 'T' : 'C'], key = s && s.contra && s.contra[0];
    if (!key) return null;
    var parts = String(key).split(':'), o = findOpt(findQ(cfg, parts[0]), parts[1]);
    return o && o.evidence ? { key: key, question: parts[0], option: parts[1], text: o.evidence } : null;
  }

  /** Value for the "Matching N named practice patterns" scoring row: only a real match is shown as a pattern. */
  function patternValue(content, res) {
    var names = [];
    ['tempo', 'connection'].forEach(function (f) { (content.diagnoses[f] || []).forEach(function (d) { names.push(d.name); }); });
    if (res.mode === 'single' && names.indexOf(res.diagnosis.name) !== -1) return res.diagnosis.name;
    if (res.mode === 'combined' && res.subDiagnoses && names.indexOf(res.subDiagnoses.tempo.name) !== -1 && names.indexOf(res.subDiagnoses.connection.name) !== -1) {
      return 'Two: ' + res.subDiagnoses.tempo.short + ' + ' + res.subDiagnoses.connection.short;
    }
    if (res.mode === 'maintenance') return 'None needed: ' + res.diagnosis.name;
    return 'No single match: starting focus';
  }

  /** Halfway → final line. Shown only for single/combined results the golfer did not edit, and only claims what the
      last 4 answers (positions 7-10) actually contain. */
  function continuityLine(cfg, answers, order, lean, res) {
    if (lean !== 'tempo' && lean !== 'connection') return '';
    if (res.mode !== 'single' && res.mode !== 'combined') return '';
    var last = tally(cfg, answers, order.slice(6)), n = { tempo: last.t, connection: last.c };
    var other = lean === 'tempo' ? 'connection' : 'tempo';
    if (res.mode === 'combined') {
      return n[other] >= 1 ? 'At halfway you leaned toward ' + lean + '. Your last 4 answers brought ' + other + ' level with it.'
        : 'At halfway you leaned toward ' + lean + '. With all 10 answers, ' + other + ' is level with it.';
    }
    var fin = res.evidenceFamily || res.family;
    if (fin === lean) {
      return n[lean] >= 1 ? 'At halfway your answers leaned toward ' + lean + '. Your last 4 answers confirmed it.'
        : 'At halfway your answers leaned toward ' + lean + ', and all 10 still point there.';
    }
    return n[fin] >= 1 ? 'At halfway your answers leaned toward ' + lean + '. Your last 4 answers shifted it to ' + fin + '.'
      : 'At halfway your answers leaned toward ' + lean + '. With all 10 answers, ' + fin + ' comes out ahead.';
  }

  /** The PDP's bundle box always says "your answers pointed to both", so a bundle hand-off is only written when it
      really carries both priorities. */
  function handOffFitsChoice(handOff, choice) {
    return !!handOff && (choice !== 'bundle' || handOff.priorities.length === 2);
  }

  function clip80(s) { s = String(s || ''); return s.length > 80 ? s.slice(0, 79) + '…' : s; }

  /** Credit fields of the hand-off, for the product page bubble. credit: {code, amount, minimum, currency} or a bare code. */
  function handOffCredit(credit) {
    var c = credit && typeof credit === 'object' ? credit : { code: credit };
    var code = String(c.code || '').trim(), amt = +c.amount, min = +c.minimum;
    if (!code) return { creditCode: null, creditAmount: null, creditMinimum: null, creditCurrency: null };
    return { creditCode: code, creditAmount: amt > 0 ? amt : null, creditMinimum: amt > 0 ? (min > 0 ? min : 0) : null,
      creditCurrency: c.currency ? String(c.currency).toUpperCase() : null };
  }

  /** sessionStorage hand-off read by golf-prescription.js validateAssessment(). choice: tempo | band | bundle.
      credit: the visitor's credit ({code, amount, minimum, currency}); null when the credit is off. */
  function buildHandOff(cfg, content, result, choice, credit, now) {
    var answers = result.answers || {};
    var DIM = { tempo: 'T', band: 'C' }, FAM = { tempo: 'tempo', band: 'connection' };
    function idsFor(dim) {
      var ids = [], all = [];
      (result.evidence || []).forEach(function (e) {
        if (all.indexOf(e.question) === -1) all.push(e.question);
        var o = findOpt(findQ(cfg, e.question), e.option || answers[e.question]);
        if (w(o, dim) >= 2 && ids.indexOf(e.question) === -1) ids.push(e.question);
      });
      ids = ids.slice(0, 3);
      if (!ids.length) ids = all.slice(0, 3);
      return ids.length ? ids : ['q1'];
    }
    function pr(key, name) { return { product: key, name: clip80(name), answerIds: idsFor(DIM[key]) }; }
    function nameFor(key) {
      var fam = FAM[key];
      if (result.mode === 'combined' && result.subDiagnoses) return result.subDiagnoses[fam].short;
      if (result.family === fam) return result.diagnosis.short;
      return content.fallbacks[fam].short;
    }
    var pri;
    if (choice === 'bundle') {
      if (result.mode === 'combined' && result.subDiagnoses) {
        pri = [pr('tempo', result.subDiagnoses.tempo.short), pr('band', result.subDiagnoses.connection.short)];
      } else if (result.family === 'connection') {
        pri = [pr('band', result.diagnosis.short)];
        if (isHigh(result.levels.T)) pri.push(pr('tempo', content.fallbacks.tempo.short));
      } else {
        pri = [pr('tempo', result.diagnosis.short)];
        if (isHigh(result.levels.C)) pri.push(pr('band', content.fallbacks.connection.short));
      }
    } else {
      pri = [pr(choice, nameFor(choice))];
    }
    var hc = handOffCredit(credit);
    return {
      version: 1, completed: true, completedAt: new Date(now || Date.now()).toISOString(),
      answers: cfg.questions.map(function (q) {
        var o = findOpt(q, answers[q.id]);
        return { questionId: q.id, question: q.text, answer: o ? o.label : '' };
      }),
      priorities: pri,
      creditCode: hc.creditCode,
      creditAmount: hc.creditAmount,
      creditMinimum: hc.creditMinimum,
      creditCurrency: hc.creditCurrency
    };
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function icon(name, cls) {
    var p = ICONS[name];
    return p ? '<svg class="' + (cls || 'gq-i') + '" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + p + '</svg>' : '';
  }
  var MONO = '<svg class="gq-mono" viewBox="0 0 32 32" width="32" height="32" aria-hidden="true" focusable="false"><circle cx="16" cy="16" r="15.5" fill="#141517"/><text x="16" y="20.6" text-anchor="middle" fill="#FFFFFF" font-size="13" font-weight="700" style="font-family:var(--gq-font-display)">DG</text></svg>';

  /* ================= BROWSER APP ================= */
  function boot(win) {
    var doc = win.document, DG = win.DrGolf || {};
    var cfg = DG.config, content = DG.content, engine = DG.engine;
    var analytics = DG.analytics || { init: function () {}, track: function () {} };
    var app = doc.getElementById('gq-app');
    if (!app || !cfg || !content || !engine || app.getAttribute('data-booted')) return;
    app.setAttribute('data-booted', '1');

    var Q = cfg.questions, N = Q.length, byId = {};
    Q.forEach(function (q) { byId[q.id] = q; });
    var data = {};
    try { data = JSON.parse(doc.getElementById('gq-data').textContent) || {}; } catch (e) { data = {}; }
    /* Credit comes only from the section settings, per market (see creditFor). */
    var credit = creditFor(data), creditOn = !!credit.code;
    var prods = data.products || {};
    var CUR = String(data.currency || '').toUpperCase(); // the visitor's (display) currency
    var US = String(data.country || '').toUpperCase() === 'US';
    var showHalfway = data.showHalfway !== false;
    var assetBase = data.assetBase || '';
    var HALF_IMG = data.halfwayImage || assetBase + 'photo-golden-swing.jpg';
    var pol = data.policies || {};
    var mq = win.matchMedia ? win.matchMedia('(prefers-reduced-motion: reduce)') : null;
    function reduced() { return !!(mq && mq.matches); }
    var params = new URLSearchParams(win.location.search);
    var urlAngle = params.get('angle') ? normAngle(params.get('angle')) : null;
    var SITE = 'Dr.Golf Swing Test';

    /* ---------- money (visitor currency; whenever the credit is on it is in this same currency) ---------- */
    var NF = moneyFormat(CUR, 2);
    function fmt(c) { return NF.format(c / 100); }
    function whole(d) { return formatWhole(d, CUR); }
    var AMT = whole(credit.amount), MIN = whole(credit.minimum);

    /* ---------- state ---------- */
    var KEY = 'drgolf.quiz.v2', TTL = 14 * 864e5, mem = null, canStore = true;
    function blank(angle) {
      return { v: 2, sid: newSid(), order: buildOrder(angle), angle: angle, campaign: {}, answers: {}, startedAt: null, completedAt: null,
        halfwayLean: null, edited: false, prefer: null, preferFor: null, tracked: {}, updatedAt: Date.now() };
    }
    function newSid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
    function load() {
      var s;
      try { s = JSON.parse(win.localStorage.getItem(KEY) || 'null'); } catch (e) { canStore = false; s = mem; }
      if (!s || s.v !== 2 || !validOrder(s.order) || !s.updatedAt || Date.now() - s.updatedAt > TTL) return null;
      s.answers = s.answers || {}; s.tracked = s.tracked || {}; s.campaign = s.campaign || {};
      if (!s.sid) s.sid = newSid();
      return s;
    }
    function save() {
      state.updatedAt = Date.now();
      var j = JSON.stringify(state);
      mem = JSON.parse(j);
      if (canStore) { try { win.localStorage.setItem(KEY, j); } catch (e) { canStore = false; } }
    }
    var state = load() || blank(urlAngle || 'default');
    function answeredCount() { return state.order.filter(function (id) { return state.answers[id]; }).length; }
    function firstUnanswered() { for (var i = 0; i < N; i++) if (!state.answers[state.order[i]]) return i + 1; return 0; }
    function posOf(qid) { return state.order.indexOf(qid) + 1; }
    function ownString() { return engine.encodeAnswers(state.answers, Q); }
    /* The starter/maintenance focus switch only applies to the exact answer string it was made for. */
    function preferFor(str) { return state.prefer && state.preferFor === str ? state.prefer : null; }
    if (urlAngle && urlAngle !== state.angle && !answeredCount()) { state.angle = urlAngle; state.order = buildOrder(urlAngle); }
    (function campaign() {
      var found = {};
      (cfg.campaignParams || []).forEach(function (k) { var v = params.get(k); if (v) found[k] = v.slice(0, 120); });
      if (Object.keys(found).length && !Object.keys(state.campaign).length) state.campaign = found;
    })();
    save();

    var cp = state.campaign;
    analytics.init(cfg.analytics, {
      variant: 'v2', angle: state.angle, order: state.order.join(','), source: cp.utm_source || null, campaign: cp.utm_campaign || null,
      content: cp.utm_content || null, ad_id: cp.ad_id || null, offer_id: credit.code ? credit.code.toLowerCase() : 'golf10', host: 'shopify'
    });
    function track(n, p) { try { analytics.track(n, p || {}); } catch (e) { /* never break the quiz */ } }
    function trackOnce(id, n, p) { if (state.tracked[id]) return; state.tracked[id] = 1; save(); track(n, p); }

    /* ---------- router ---------- */
    var timers = [], cleanups = [], locked = false, lastRank = 0, firstRender = true, cur = {}, curRes = null, bandVid = null, sheet = null;
    function later(fn, ms) { timers.push(win.setTimeout(fn, ms)); }
    function clearAll() {
      timers.forEach(win.clearTimeout); timers = [];
      cleanups.concat(stickyOff).forEach(function (f) { try { f(); } catch (e) { /* */ } }); cleanups = []; stickyOff = [];
      locked = false;
    }
    function parse(h) {
      h = String(h || '').replace(/^#/, '');
      var m;
      if (!h || h === '/') return { name: 'q', n: 1, home: true };
      if ((m = h.match(/^\/q\/(\d{1,2})(\/r)?$/)) && +m[1] >= 1 && +m[1] <= N) return { name: 'q', n: +m[1], fromResult: !!m[2] };
      if (h === '/check') return { name: 'check' };
      if (h === '/scoring') return { name: 'scoring' };
      if ((m = h.match(/^\/result\/(.+)$/))) return { name: 'result', str: m[1] };
      return { bad: true };
    }
    function redirect(h) { try { win.history.replaceState(null, '', h); } catch (e) { win.location.replace(h); return; } route(); }
    function go(h) { if (win.location.hash === h) route(); else win.location.hash = h; }

    function route() {
      clearAll();
      closeSheet(true);
      var r = parse(win.location.hash), fu = firstUnanswered();
      if (r.bad) return redirect('#/');
      if (r.name === 'q') {
        if (!r.home && fu && fu < r.n) return redirect('#/q/' + fu);
        return screenQuestion(r);
      }
      if (r.name === 'check') {
        if (!showHalfway || (fu && fu <= 6)) return redirect('#/q/7');
        return screenCheck();
      }
      if (r.name === 'scoring') {
        if (fu) return redirect('#/q/' + fu);
        return screenScoring();
      }
      var s = r.str.toUpperCase();
      if (!/^[A-E]{10}$/.test(s) || !engine.isComplete(engine.parseAnswers(s, Q), Q)) return redirect('#/');
      return screenResult(s);
    }

    function mount(name, rank, title, html) {
      var dir = rank < lastRank ? 'back' : 'fwd';
      lastRank = rank;
      app.setAttribute('data-route', name);
      app.removeAttribute('aria-busy');
      app.innerHTML = html;
      var scr = app.firstElementChild;
      if (scr && !firstRender && !reduced()) scr.classList.add(dir === 'back' ? 'gq-in-back' : 'gq-in');
      win.scrollTo(0, 0);
      doc.title = title + ' · ' + SITE;
      if (!firstRender) { var h = app.querySelector('[data-focus]'); if (h) { try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } } }
      firstRender = false;
    }

    /* ---------- shared pieces ---------- */
    function progress(answered, ballPos, pulse) {
      var segs = '';
      for (var i = 0; i < N; i++) segs += '<span class="gq-seg' + (i < answered ? ' is-on' : '') + '"></span>';
      return '<div class="gq-prog" role="progressbar" aria-valuemin="0" aria-valuemax="' + N + '" aria-valuenow="' + answered + '" aria-label="' + answered + ' of ' + N + ' answered">' +
        '<div class="gq-track">' + segs + '<span class="gq-rail" data-to="' + ballPos + '" style="transform:translateX(' + (cur.ball || 0) + '%)"><span class="gq-ball"></span></span></div>' +
        (creditOn ? '<span class="gq-flag' + (pulse ? ' is-pulse' : '') + '">' + esc(AMT) + '</span>' : '') + '</div>';
    }
    function rollBall() {
      var rail = app.querySelector('.gq-rail');
      if (!rail) return;
      var to = +rail.getAttribute('data-to');
      win.requestAnimationFrame(function () { win.requestAnimationFrame(function () { rail.style.transform = 'translateX(' + to + '%)'; }); });
      cur.ball = to;
    }
    function topbar(backHref, count) {
      return '<header class="gq-top">' +
        (backHref ? '<a class="gq-back" href="' + backHref + '" aria-label="Previous question">' + icon('back') + '</a>' : '<span class="gq-back" aria-hidden="true"></span>') +
        MONO + '<span class="gq-count" aria-live="off">' + count + '</span></header>';
    }

    /* ---------- question screen ---------- */
    function screenQuestion(r) {
      var answered = answeredCount(), fu = firstUnanswered();
      /* A golfer with saved answers who arrives from a different ad (?angle=) sees that ad's hook and first question,
         matching the server-rendered hook. Continue resumes the saved order; answering here starts over in the new order. */
      var freshAngle = r.home && answered > 0 && urlAngle && urlAngle !== state.angle ? urlAngle : null;
      var angle = freshAngle || state.angle, order = freshAngle ? buildOrder(freshAngle) : state.order;
      var n = r.n, qid = order[n - 1], q = byId[qid];
      var banner = '';
      if (r.home && answered === N) {
        banner = '<div class="gq-resume" role="region" aria-label="Saved progress"><p>Your result is saved.</p><div class="gq-resume__btns"><button type="button" class="gq-btn gq-btn--sm" data-act="seeresult">See my result</button></div></div>';
      } else if (r.home && answered > 0) {
        banner = '<div class="gq-resume" role="region" aria-label="Saved progress"><p>Pick up at question ' + fu + ' of ' + N + '?</p><div class="gq-resume__btns"><button type="button" class="gq-btn gq-btn--sm" data-act="continue">Continue</button><button type="button" class="gq-btn gq-btn--sm gq-btn--ghost" data-act="startover">Start over</button></div></div>';
      }
      cur = { r: r, q: q, banner: !!banner, ball: cur.ball || 0 };
      var sel = banner ? null : state.answers[qid];
      var back = n === 1 ? null : r.fromResult ? '#/result/' + ownString() : (n === 7 && showHalfway) ? '#/check' : '#/q/' + (n - 1);
      var tabIdx = Math.max(0, q.options.map(function (o) { return o.id; }).indexOf(sel));
      var cards = q.options.map(function (o, i) {
        var lab = labelFor(cfg, qid, o.id), bubble = qid === 'q8', on = sel === o.id;
        return '<button type="button" role="radio" class="gq-opt' + (bubble ? ' gq-opt--bubble' : '') + (qid === 'q5' && o.id === 'E' ? ' gq-opt--unsure' : '') + (on ? ' is-on' : '') +
          '" aria-checked="' + on + '" tabindex="' + (i === tabIdx ? 0 : -1) + '" data-opt="' + o.id + '">' +
          (bubble ? '' : '<span class="gq-opt__ico" aria-hidden="true">' + (icon(lab.icon) || '<span class="gq-opt__letter">' + o.id + '</span>') + icon('check', 'gq-i gq-i--check') + '</span>') +
          '<span class="gq-opt__txt"><span class="gq-opt__label">' + esc(lab.label) + '</span>' + (lab.detail ? '<span class="gq-opt__detail">' + esc(lab.detail) + '</span>' : '') + '</span></button>';
      }).join('');
      var micro = n === 8 ? (creditOn ? '3 to go · your ' + AMT + ' credit unlocks at the end' : '3 to go') : n === 9 ? '1 more, then your result' : n === N ? (creditOn ? 'Your result and ' + AMT + ' store credit are next.' : 'Your result is next.') : '';
      var hook = n === 1 ? '<h1 class="gq-hook" data-focus tabindex="-1">' + esc(HOOKS[angle] || DEFAULT_HOOK) + '</h1><p class="gq-micro">10 questions · about 2 min' + (creditOn ? ' · ' + AMT + ' store credit when you finish' : '') + '</p>'
        : '<h1 class="gq-sr">Question ' + n + ' of ' + N + '</h1>';
      /* The coach note is part of the screen markup (no late live-region insert); the question heading points to it. */
      var note = r.home ? null : noteFor(cfg, state.order, n, state.answers, { showHalfway: showHalfway });
      var html = '<div class="gq-screen gq-q' + (n === 1 ? ' gq-q--first' : '') + '">' +
        topbar(back, n + '/' + N) + progress(answered, (n - 1) * 10, n === N) + banner + hook +
        (note ? '<div class="gq-note-slot"><div class="gq-note" id="gq-note">' + MONO + '<p><b>DR.GOLF</b> ' + esc(note.text) + '</p></div></div>' : '') +
        '<div class="gq-eyebrow-row"><p class="gq-eyebrow">' + esc(String(q.eyebrow).toUpperCase()) + (n === N ? ' · LAST ONE' : '') + '</p>' +
        '<button type="button" class="gq-why" data-act="why" aria-haspopup="dialog">' + icon('info') + '<span>Why we ask</span></button></div>' +
        '<h2 class="gq-qtext" id="gq-qt"' + (n === 1 ? '' : ' data-focus tabindex="-1"') + (note ? ' aria-describedby="gq-note"' : '') + '>' + esc(q.text) + '</h2>' +
        '<div class="gq-opts" role="radiogroup" aria-labelledby="gq-qt">' + cards + '</div>' +
        (micro ? '<p class="gq-posline">' + esc(micro) + '</p>' : '') +
        (n === 1 ? '<p class="gq-foot">Free · No email needed · A practice priority based on your answers, not a swing-video analysis' + (pol.privacy ? ' · <a href="' + esc(pol.privacy) + '">Privacy</a>' : '') + '</p>' : '') +
        '</div>';
      mount('q', n, 'Question ' + n + ' of ' + N, html);
      rollBall();
      if (n === 5 && showHalfway) { try { new win.Image().src = HALF_IMG; } catch (e) { /* */ } }
      // One landing view per saved state (sid), not per return to Q1 (Back from Q2, redirects, resume).
      if (n === 1) trackOnce('landing:' + state.sid, 'assessment_landing_view', {});
      if (banner) track('resume_shown', { answered: answered });
    }

    function resetAnswers() {
      state.answers = {}; state.startedAt = null; state.completedAt = null; state.halfwayLean = null; state.edited = false;
      state.prefer = null; state.preferFor = null;
      state.restarted = true;
      // A new ad (?angle=) restarts in that ad's hook and question order.
      if (urlAngle && urlAngle !== state.angle) {
        state.angle = urlAngle; state.order = buildOrder(urlAngle);
        if (analytics.setBase) { try { analytics.setBase({ angle: state.angle, order: state.order.join(',') }); } catch (e) { /* */ } }
      }
    }

    function pick(btn) {
      if (locked || !cur.q) return;
      locked = true;
      var r = cur.r, q = cur.q, opt = btn.getAttribute('data-opt');
      if (cur.banner) { resetAnswers(); cur.banner = false; track('resume_restart', { implicit: true }); var b = app.querySelector('.gq-resume'); if (b) b.remove(); }
      var prev = state.answers[q.id];
      Array.prototype.forEach.call(app.querySelectorAll('.gq-opt'), function (b) { b.classList.remove('is-on'); b.setAttribute('aria-checked', 'false'); });
      btn.classList.add('is-on', 'is-tap'); btn.setAttribute('aria-checked', 'true');
      if (!state.startedAt) { state.startedAt = Date.now(); track('assessment_started', { restart: !!state.restarted }); state.restarted = false; }
      if (prev && prev !== opt && (state.completedAt || r.fromResult)) state.edited = true;
      if (prev !== opt) { state.prefer = null; state.preferFor = null; }
      state.answers[q.id] = opt;
      save();
      track('assessment_question_answered', { question: q.id, index: r.n, answer: opt, changed: r.fromResult ? true : !!(prev && prev !== opt) });
      var n = r.n, next = r.fromResult && !firstUnanswered() ? '#/result/' + ownString()
        : n === 6 ? (showHalfway ? '#/check' : '#/q/7') : n === N ? '#/scoring' : '#/q/' + (n + 1);
      later(function () { if (r.fromResult) redirect(next); else go(next); }, reduced() ? 120 : 280);
    }

    /* ---------- bottom sheet ---------- */
    function openSheet(title, text, trigger) {
      closeSheet(true);
      var wrap = doc.createElement('div');
      wrap.className = 'gq-sheet-wrap';
      wrap.innerHTML = '<div class="gq-backdrop" data-close></div><div class="gq-sheet" role="dialog" aria-modal="true" aria-labelledby="gq-sheet-t">' +
        '<div class="gq-sheet__head"><h2 id="gq-sheet-t" class="gq-sheet__t" tabindex="-1">' + esc(title) + '</h2><button type="button" class="gq-close" data-close aria-label="Close">' + icon('close') + '</button></div><p>' + esc(text) + '</p></div>';
      doc.body.appendChild(wrap);
      app.setAttribute('inert', '');
      sheet = { el: wrap, trigger: trigger };
      wrap.addEventListener('click', function (e) { if (e.target.closest('[data-close]')) closeSheet(); });
      wrap.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); closeSheet(); }
        if (e.key === 'Tab') {
          // Trap built from tabbable elements only (the tabindex=-1 heading is never reached by Tab).
          var f = [].filter.call(wrap.querySelectorAll('button, [href], [tabindex]'), function (el) { return !el.disabled && el.getAttribute('tabindex') !== '-1'; });
          var first = f[0], last = f[f.length - 1], inside = wrap.contains(doc.activeElement);
          if (!first) { e.preventDefault(); return; }
          if (f.length === 1 || !inside) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
          else if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      wrap.querySelector('.gq-close').focus();
      win.requestAnimationFrame(function () { wrap.classList.add('is-open'); });
    }
    function closeSheet(silent) {
      if (!sheet) return;
      var s = sheet; sheet = null;
      s.el.remove();
      app.removeAttribute('inert');
      if (!silent && s.trigger && doc.contains(s.trigger)) s.trigger.focus();
    }

    /* ---------- halfway ---------- */
    function screenCheck() {
      var tl = tally(cfg, state.answers, state.order.slice(0, 6)), lean = leanOf(tl.t, tl.c);
      state.halfwayLean = lean; save();
      var o6 = findOpt(byId.q6, state.answers.q6), img = (cfg.imagery && cfg.imagery.analysis) || {};
      function dots(n, cls) { var s = ''; for (var i = 0; i < 6; i++) s += '<span class="gq-dot6' + (i < n ? ' is-on ' + cls : '') + '" style="--i:' + i + '"></span>'; return s; }
      var leanLine = lean === 'tempo' ? 'So far your answers lean toward <b>tempo</b>.' : lean === 'connection' ? 'So far your answers lean toward <b>connection</b>.'
        : lean === 'split' ? 'It’s a split so far.' : 'No strong signal yet, which is common and useful.';
      var html = '<div class="gq-screen gq-check">' + topbar('#/q/6', '6/' + N + ' ✓') +
        '<img class="gq-band-img" src="' + esc(HALF_IMG) + '" alt="' + esc(img.alt || '') + '" width="900" height="400">' +
        '<div class="gq-check__body"><p class="gq-pill">HALFWAY · 6 OF 10 ANSWERED</p>' +
        '<h1 class="gq-check__h" data-focus tabindex="-1">YOUR BEST SWING IS ALREADY IN THERE.</h1>' +
        (o6 && o6.echo ? '<p class="gq-ref">Your reference swing: “<b>' + esc(capFirst(o6.echo)) + '.</b>”</p>' : '') +
        '<p>The goal isn’t a new swing. It’s finding out what changes when that feeling disappears.</p>' +
        '<p class="gq-check__teach">This test looks at the two things that most often make a swing come and go:</p>' +
        '<div class="gq-teach"><div><b>TEMPO</b><span>the pace of your swing, especially the change of direction at the top.</span></div><div><b>CONNECTION</b><span>whether your arms and body move together.</span></div></div>' +
        '<div class="gq-tally"><p class="gq-eyebrow">YOUR ANSWERS SO FAR</p>' +
        '<div class="gq-tally__row"><span>Tempo signals: ' + tl.t + '</span><span class="gq-dots" aria-hidden="true">' + dots(tl.t, 'is-lime') + '</span></div>' +
        '<div class="gq-tally__row"><span>Connection signals: ' + tl.c + '</span><span class="gq-dots" aria-hidden="true">' + dots(tl.c, 'is-mint') + '</span></div>' +
        '<p class="gq-lean">' + leanLine + '</p><p class="gq-fine">Based on 6 of 10 answers. The last 4 include the two most direct questions, about your transition and your arms, so this can still change.</p></div>' +
        '<a class="gq-btn gq-btn--ondark" href="#/q/7" data-act="halfway-next">NEXT 4 QUESTIONS<span class="gq-btn__arr" aria-hidden="true">→</span></a>' +
        (creditOn ? '<p class="gq-fine gq-center">Your ' + AMT + ' store credit unlocks when you finish.</p>' : '') + '</div></div>';
      mount('check', 6.5, 'Halfway', html);
      track('halfway_viewed', { t: tl.t, c: tl.c, lean: lean });
    }

    /* ---------- scoring ---------- */
    function nPatterns() { return content.diagnoses.tempo.length + content.diagnoses.connection.length; }
    function screenScoring() {
      if (!state.completedAt) { state.completedAt = Date.now(); save(); }
      var str = ownString(), res = engine.evaluate(str, cfg, content, { preferFamily: preferFor(str) });
      trackOnce('done:' + str, 'assessment_completed', {
        duration_s: state.startedAt ? Math.round((state.completedAt - state.startedAt) / 1000) : null,
        diagnosis: res.diagnosis.id, family: res.family, mode: res.mode, strength: res.strength
      });
      var ce = counterFor(cfg, res), proto = content.protocols[res.protocol] || {};
      var rows = [
        ['Tempo signals', res.support.T.count ? res.support.T.count + ' of your answers' : 'None strong'],
        ['Connection signals', res.support.C.count ? res.support.C.count + ' of your answers' : 'None strong'],
        ['Cross-checking answers that disagree', ce ? 'Found one (Q' + posOf(ce.question) + '), shown in your result' : res.mode === 'maintenance' ? 'Not needed for a stable swing' : 'None found'],
        ['Matching ' + nPatterns() + ' named practice patterns', patternValue(content, res)],
        ['Choosing your practice routine', proto.title || '']
      ];
      var html = '<div class="gq-screen gq-scoring">' + '<div class="gq-scoring__mono">' + MONO + '</div>' +
        '<h1 class="gq-scoring__h" data-focus tabindex="-1">SCORING YOUR 10 ANSWERS</h1><ol class="gq-rows">' +
        rows.map(function (rw) {
          return '<li class="gq-row"><span class="gq-row__st" aria-hidden="true"><span class="gq-spin"></span><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path class="gq-tick" d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>' +
            '<span class="gq-row__l">' + esc(rw[0]) + '</span><span class="gq-row__v">' + esc(rw[1]) + '</span></li>';
        }).join('') + '</ol><p class="gq-sr" aria-live="polite" id="gq-live"></p>' +
        '<p class="gq-fine gq-center">Rule-based scoring of what you told us, not a video analysis. Same answers, same result, every time.</p>' +
        '<p class="gq-center"><button type="button" class="gq-link gq-skip" data-act="skip" hidden>Show my result now</button></p></div>';
      mount('scoring', 11, 'Scoring your answers', html);
      // aria-busy sits on the visual list only, so the sibling live region still announces each row.
      var ol = app.querySelector('.gq-rows'); if (ol) ol.setAttribute('aria-busy', 'true');
      var lis = app.querySelectorAll('.gq-row'), live = app.querySelector('#gq-live'), done = 0;
      cur = { rowsDone: 0, str: str };
      function tick(i) { lis[i].classList.add('is-done'); cur.rowsDone = ++done; live.textContent = rows[i][0] + ': ' + rows[i][1]; }
      function finish() { if (ol) ol.removeAttribute('aria-busy'); redirect('#/result/' + str); }
      if (reduced()) {
        for (var i = 0; i < lis.length; i++) { lis[i].classList.add('is-shown'); tick(i); }
        later(finish, 1200);
        return;
      }
      rows.forEach(function (rw, i) {
        later(function () { lis[i].classList.add('is-shown'); }, i * 600);
        later(function () { tick(i); if (i === rows.length - 1) later(finish, 400); }, i * 600 + 350);
      });
      later(function () { var s = app.querySelector('.gq-skip'); if (s) s.hidden = false; }, 1500);
    }

    /* ---------- products ---------- */
    function P(k) { return prods[k] || null; }
    function bandVariant() {
      var p = P('band'); if (!p) return null;
      var vs = p.variants || [];
      return vs.filter(function (v) { return v.id === bandVid; })[0] || firstAvail(vs);
    }
    function variantOf(k) {
      var p = P(k); if (!p) return null;
      if (k === 'band') return bandVariant();
      if (k === 'bundle') { var bv = bandVariant(); return bundleVariantFor(p, bv ? bv.option1 : null); }
      return firstAvail(p.variants);
    }
    function priceOf(k) { var v = variantOf(k), p = P(k); return v && typeof v.price === 'number' ? v.price : p && typeof p.price === 'number' ? p.price : null; }
    function compareOf(k) { var v = variantOf(k), p = P(k); return v ? v.compare_at_price : p ? p.compare_at_price : null; }
    /* Missing product data (all_products lookup failed) is "unknown", not "unavailable": the CTA still goes to the
       product page (fallback URL, no price). Only Shopify's own available:false hides a CTA. */
    function availOf(k) { var p = P(k), v = variantOf(k); return !p || (p.available !== false && (!v || v.available !== false)); }
    function hrefOf(k) { var p = P(k), v = variantOf(k); return discountHref(credit.code, (p && p.url) || FALLBACK_URL[k], v ? v.id : null); }
    function nameOf(k) { return (P(k) && P(k).title) || FALLBACK_NAME[k]; }
    function credited(k) { var c = priceOf(k); return creditApplies(c, credit, CUR) ? withCredit(c, credit) : null; }
    function priceRow(k, mode) {
      var p = P(k), c = priceOf(k);
      if (!p || c == null) return '<p class="gq-fine">Price shown on the product page</p>';
      var ca = compareOf(k), wc = credited(k);
      var h = '<span class="gq-price">' + fmt(c) + '</span>' + (typeof ca === 'number' && ca > c ? ' <s class="gq-was">' + fmt(ca) + '</s>' : '');
      if (wc != null) h += mode === 'arrow' ? ' <span class="gq-arrow">→ <b>' + fmt(wc) + ' with your credit</b></span>' : ' <span class="gq-pill">With your credit: ' + fmt(wc) + '</span>';
      return '<div class="gq-pricerow">' + h + '</div>';
    }
    function stars(k) {
      var p = P(k), r = p && parseFloat(p.rating), n = p && parseInt(p.rating_count, 10);
      if (!r || !n) return '';
      var s = '';
      for (var i = 1; i <= 5; i++) s += '<span class="gq-star' + (r >= i - 0.25 ? ' is-on' : '') + '">★</span>';
      return '<p class="gq-stars"><span aria-hidden="true">' + s + '</span> <span class="gq-sr">Rated ' + r.toFixed(1) + ' out of 5</span>(' + n + ')</p>';
    }
    function imgOf(k) {
      var p = P(k), cp = cfg.products[k] || {};
      var src = p && p.image ? p.image : cp.image ? assetBase + cp.image : null;
      return src ? '<img class="gq-pimg" src="' + esc(src) + '" alt="' + esc((p && p.image_alt) || cp.imageAlt || nameOf(k)) + '" width="600" height="450" loading="lazy">' : '';
    }
    function forWont(keys) {
      var pc = content.productCopy, fo = [], wo = [];
      if (keys.length === 1) {
        fo = pc[keys[0]].features.slice(0, 3).map(function (f) { return f.t; });
        wo = pc[keys[0]].limits.slice(0, 2);
      } else {
        fo = [pc.tempo.features[0].t, pc.band.features[1].t];
        wo = [pc.tempo.limits[1], pc.band.limits[0]];
      }
      function li(a) { return '<ul>' + a.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('') + '</ul>'; }
      return '<div class="gq-cols"><div><p class="gq-eyebrow">WHAT IT’S FOR</p>' + li(fo) + '</div><div><p class="gq-eyebrow">WHAT IT WON’T DO</p>' + li(wo) + '</div></div>';
    }
    function cardTop(k) {
      var pc = content.productCopy[k] || {};
      return imgOf(k) + '<p class="gq-eyebrow">' + esc(pc.eyebrow || '') + '</p><h3 class="gq-pname">' + esc(nameOf(k)) + '</h3>' +
        (pc.tagline ? '<p class="gq-tagline">' + esc(pc.tagline) + '</p>' : '') + stars(k);
    }
    function cta(k, loc, text, cls) {
      var arrow = / →$/.test(text);
      return '<a class="gq-btn' + (cls ? ' ' + cls : '') + '" href="' + esc(hrefOf(k)) + '" data-cta="' + k + '" data-loc="' + loc + '">' + esc(arrow ? text.slice(0, -2) : text) + (arrow ? '<span class="gq-btn__arr" aria-hidden="true">→</span>' : '') + '</a>';
    }
    function swatches() {
      var p = P('band'); if (!p || !p.variants || p.variants.length < 2) return '';
      var sel = bandVariant();
      return '<div class="gq-sw" role="radiogroup" aria-label="Band ' + (US ? 'color' : 'colour') + '">' + p.variants.map(function (v) {
        var on = sel && v.id === sel.id, name = v.option1 || v.title, hex = /blue/i.test(name) ? '#1e9be0' : /black/i.test(name) ? '#111' : '#85817A';
        return '<button type="button" role="radio" class="gq-sw__b' + (on ? ' is-on' : '') + '" aria-checked="' + !!on + '" tabindex="' + (on ? 0 : -1) + '" data-vid="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' +
          '<span class="gq-sw__dot" style="background:' + hex + '"></span><span>' + esc(name) + (v.available ? '' : ' — sold out') + '</span></button>';
      }).join('') + '</div>';
    }
    function quoteOf(a) { return a ? labelFor(cfg, a.question, a.option).label : null; }
    function unavailable(alts) {
      var a = alts.filter(availOf)[0];
      return '<p class="gq-unavail">Currently unavailable</p>' + (a ? cta(a, 'alt', 'SEE THE ' + nameOf(a).toUpperCase() + ' →', 'gq-btn--sec') : '') +
        '<p class="gq-fine">Your free drill below still works without it.</p>';
    }

    function toolTempo(res) {
      var wc = credited('tempo'), out = '<div class="gq-card">' + cardTop('tempo') + '<p class="gq-eyebrow">WHY THIS FITS YOU</p><p>' + esc(whyText(cfg, res, 'tempo')) + '</p>' + forWont(['tempo']) + priceRow('tempo');
      out += availOf('tempo') ? '<div class="gq-ctazone">' + cta('tempo', 'card', 'GET MY TEMPO STICK' + (wc != null ? ' · CREDIT APPLIED' : '') + ' →') +
        (creditOn ? '<p class="gq-fine">Opens the product page with your ' + AMT + ' credit applied. One per customer.</p>' : '') + '</div>' : unavailable(['bundle']);
      out += '</div>';
      if (isHigh(res.levels.C) && P('bundle') && availOf('bundle')) {
        var q = quoteOf(strongestAnswer(cfg, res.answers, 'C', state.order, 2)), b = priceOf('bundle'), bw = credited('bundle');
        out += '<a class="gq-upsell" href="' + esc(hrefOf('bundle')) + '" data-cta="bundle" data-loc="upsell">' +
          (q ? 'You also said “' + esc(q) + '”. ' : '') + 'Add the Connection Band: Bundle ' + (b != null ? fmt(b) : '') +
          (bw != null ? ' → <b>' + fmt(bw) + ' with your credit</b>' : '') + (data.bundleShippingNote ? ' · ' + esc(data.bundleShippingNote) : '') + ' →</a>';
      }
      return out;
    }
    function savingLine() {
      var s = separateSaving(priceOf('tempo'), priceOf('band'), priceOf('bundle'));
      if (!s) return '';
      // With the credit applied the bundle saves the separate-price difference plus the credit amount.
      if (creditOn && credited('bundle') != null) return '<p class="gq-fine">' + whole((s / 100) + credit.amount) + ' less than buying both separately, with your credit</p>';
      return '<p class="gq-fine">' + whole(s / 100) + ' less than buying both separately</p>';
    }
    function toolBand(res) {
      var bv = bandVariant(), colour = bv ? (bv.option1 || bv.title) : '', bandP = priceOf('band'), bwc = credited('bundle'), bandWc = credited('band');
      var out = '<div class="gq-card">' + cardTop('band') + '<p class="gq-eyebrow">WHY THIS FITS YOU</p><p>' + esc(whyText(cfg, res, 'band')) + '</p>' + forWont(['band']) + priceRow('band') + swatches() + '</div>';
      out += '<h3 class="gq-h3" id="gq-opts-h" tabindex="-1">' + (creditOn ? 'HOW TO USE YOUR ' + esc(AMT) + ' CREDIT' : 'YOUR OPTIONS') + '</h3><div class="gq-tiles gq-ctazone">';
      var tq = quoteOf(strongestAnswer(cfg, res.answers, 'T', state.order, 2));
      // "didn't stand out" only when the tempo reading is actually low; a moderate/high reading is never denied.
      var rel = isHigh(res.levels.T) && tq ? 'Your answers also flagged tempo: you said “' + esc(tq) + '”. The stick covers that side.'
        : isHigh(res.levels.T) ? 'Your answers show a ' + esc(String(content.levelWords[res.levels.T] || res.levels.T).toLowerCase()) + ' tempo reading. The stick covers that side.'
        : 'Tempo didn’t stand out in your answers. The stick is a second tool for later, so skip it if you only want the band.';
      out += '<div class="gq-tile' + (creditOn ? ' gq-tile--credit' : '') + '">' + (creditOn ? '<span class="gq-badge">USES YOUR ' + esc(AMT) + ' CREDIT</span>' : '') + '<p class="gq-tile__t">Dr.Golf Bundle: Connection Band + Tempo Swing Stick</p>' +
        priceRow('bundle', 'arrow') + (data.bundleShippingNote ? '<p class="gq-fine">' + esc(data.bundleShippingNote) + '</p>' : '') + savingLine() +
        (colour ? '<p class="gq-fine">Band ' + (US ? 'color' : 'colour') + ': ' + esc(colour) + '</p>' : '') + '<p class="gq-rel">' + rel + '</p>' +
        (availOf('bundle') ? cta('bundle', 'choice', 'GET THE BUNDLE' + (priceOf('bundle') != null ? ' · ' + fmt(bwc != null ? bwc : priceOf('bundle')) : '') + ' →') : unavailable(['tempo'])) + '</div>';
      out += '<div class="gq-tile"><p class="gq-tile__t">Connection Band only' + (bandP != null ? ' · ' + fmt(bandP) : '') + '</p>' +
        (!creditOn ? '' : bandWc != null ? '<p class="gq-fine">Uses your credit: ' + fmt(bandWc) + '</p>'
          : '<p class="gq-fine">Your credit needs a ' + MIN + '+ order, so it won’t apply to the band on its own. It stays attached to your checkout and applies automatically if your order reaches ' + MIN + '.</p>') +
        (availOf('band') ? cta('band', 'choice', 'GET THE BAND' + (bandP != null ? ' · ' + fmt(bandP) : '') + ' →', 'gq-btn--sec') : unavailable(['bundle', 'tempo'])) + '</div></div>';
      out += '<p class="gq-helper">' + (creditOn ? 'You don’t need both to start. The band is your tool; the bundle is simply the way your credit applies.' : 'You don’t need both to start. The band is your tool; the bundle adds the stick.') + '</p>';
      return out;
    }
    function toolCombined(res) {
      var bwc = credited('bundle'), twc = credited('tempo'), bp = priceOf('band');
      var out = '<div class="gq-card gq-kit"><h3 class="gq-pname">YOUR KIT: RHYTHM + CONNECTION</h3>' + imgOf('bundle') + stars('bundle') +
        '<p class="gq-eyebrow">WHY THIS FITS YOU</p><p>' + esc(whyText(cfg, res, 'tempo')) + '</p><p>' + esc(whyText(cfg, res, 'band')) + '</p>' + forWont(['tempo', 'band']) +
        priceRow('bundle', 'arrow') + (data.bundleShippingNote ? '<p class="gq-fine">' + esc(data.bundleShippingNote) + '</p>' : '') + savingLine() + swatches();
      out += availOf('bundle') ? '<div class="gq-ctazone">' + cta('bundle', 'card', 'GET MY KIT' + (bwc != null ? ' · CREDIT APPLIED' : '') + ' →') + '</div>' : unavailable(['tempo', 'band']);
      out += '</div><p class="gq-alt">Prefer one tool? ' +
        (availOf('tempo') ? '<a href="' + esc(hrefOf('tempo')) + '" data-cta="tempo" data-loc="alt">' + esc(nameOf('tempo')) + ' ' + (twc != null ? fmt(twc) + ' with credit' : priceOf('tempo') != null ? fmt(priceOf('tempo')) : '') + '</a>' : '') +
        (availOf('tempo') && availOf('band') ? ' · ' : '') +
        (availOf('band') ? '<a href="' + esc(hrefOf('band')) + '" data-cta="band" data-loc="alt">' + esc(nameOf('band')) + (bp != null ? ' ' + fmt(bp) : '') + (creditOn && credited('band') == null ? ' (credit needs ' + MIN + '+)' : '') + '</a>' : '') + '</p>';
      return out;
    }
    function toolInner(res) {
      var out = '<h2 class="gq-h2" id="gq-tool-h" tabindex="-1">YOUR TRAINING TOOL</h2>';
      if (!P('tempo') && !P('band') && !P('bundle')) return out + '<p>Our training tools are shown on the product pages. <a href="/collections/all">See the tools</a></p>';
      if (res.mode === 'starter' || res.mode === 'maintenance') {
        out += '<div class="gq-switch"><p>Your answers don’t strongly ' + (US ? 'favor' : 'favour') + ' one focus, so it’s your call.</p><button type="button" class="gq-link" data-act="switch">Start with ' +
          (res.alternateFamily === 'connection' ? 'arm–body connection' : 'rhythm') + ' instead</button></div>';
      }
      if (res.products.length > 1) return out + toolCombined(res);
      return out + (res.products[0] === 'band' ? toolBand(res) : toolTempo(res));
    }
    function stickyInner(res) {
      var k = res.products.length > 1 ? 'bundle' : res.products[0];
      if (!P(k) && k !== 'band') return '';
      if (k === 'band') {
        if (!P('band') && !P('bundle')) return '';
        var bw = credited('bundle'), bp = priceOf('band');
        return '<span class="gq-sticky__t">' + (bw != null ? 'Bundle ' + fmt(bw) + ' with credit' : 'Bundle or band') + (bp != null ? ' · or band ' + fmt(bp) : '') + '</span>' +
          '<button type="button" class="gq-btn gq-btn--sm" data-scroll="gq-opts-h">SEE OPTIONS ↑</button>';
      }
      if (!availOf(k)) return '';
      var wc = credited(k), price = wc != null ? fmt(wc) + ' with credit' : priceOf(k) != null ? fmt(priceOf(k)) : '';
      var label = k === 'bundle' ? 'Your kit · ' + price : esc(nameOf('tempo')) + ' · ' + price;
      var th = P(k).image ? '<img src="' + esc(P(k).image) + '" alt="" width="40" height="40" loading="lazy">' : '';
      return th + '<span class="gq-sticky__t">' + label + '</span>' + cta(k, 'sticky', 'GET IT →', 'gq-btn--sm');
    }

    /* ---------- results ---------- */
    var SIG = { T: 'TEMPO SIGNAL', C: 'CONNECTION SIGNAL', P: 'PRESSURE SIGNAL', E: 'EFFORT SIGNAL', O: 'MENTAL-LOAD SIGNAL' };
    function signalOf(qid, o) {
      if (qid === 'q6') return 'YOUR REFERENCE SWING';
      var best = null, bw = 0;
      ['T', 'C', 'P', 'E', 'O'].forEach(function (d) { if (w(o, d) > bw) { bw = w(o, d); best = d; } });
      return best ? SIG[best] : null;
    }
    function list(x, ordered) {
      if (!x) return '';
      if (!Array.isArray(x)) return '<p>' + esc(x) + '</p>';
      var t = ordered ? 'ol' : 'ul';
      return '<' + t + '>' + x.map(function (s) { return '<li>' + (typeof s === 'string' ? esc(s) : '<b>' + esc(s.t) + '</b> ' + esc(s.d || '')) + '</li>'; }).join('') + '</' + t + '>';
    }
    function drill(a) { return a ? '<h3 class="gq-h3">' + esc(a.title) + '</h3><p>' + esc(a.lead) + '</p>' + list(a.steps, true) : ''; }

    function screenResult(str) {
      if (!answeredCount()) { state.answers = engine.parseAnswers(str, Q); save(); }
      var mine = ownString() === str, pf = preferFor(str);
      var res = engine.evaluate(str, cfg, content, { preferFamily: pf });
      curRes = res;
      cur = { str: str };
      if (bandVid == null) { var fb = firstAvail(P('band') && P('band').variants); bandVid = fb ? fb.id : null; }
      trackOnce('result:' + str + ':' + (pf || ''), 'assessment_result_viewed', {
        diagnosis: res.diagnosis.id, family: res.family, mode: res.mode, strength: res.strength, secondary: res.secondary.map(function (s) { return s.id; }).join(',')
      });
      var d = res.diagnosis, sn = content.strengthNotes[res.strength] || {};
      var eyebrow = { single: 'YOUR #1 PRACTICE PRIORITY', combined: 'YOUR TWO PRACTICE PRIORITIES', starter: 'YOUR STARTING FOCUS', maintenance: 'GOOD NEWS: YOUR SWING SOUNDS FAIRLY STABLE' }[res.mode];
      // Halfway → final line: single/combined only (starter/maintenance are an early indication, and a golfer's own
      // focus switch is never reported as something their answers did).
      var cont = mine && !state.edited ? continuityLine(cfg, state.answers, state.order, state.halfwayLean, res) : '';
      var h = '<div class="gq-screen gq-result">';
      // R1
      h += '<header class="gq-top gq-top--res">' + MONO + '<span class="gq-top__title">Your result</span><button type="button" class="gq-link gq-retake" data-act="retake">Retake</button></header>';
      // R2
      if (creditOn) h += '<button type="button" class="gq-strip" data-scroll="gq-credit"><span class="gq-pill gq-pill--shine">✓ UNLOCKED</span><span>' + esc(AMT) + ' store credit, applied automatically at checkout on orders of ' + esc(MIN) + '+.</span></button>';
      // R3
      h += '<section class="gq-hero" id="gq-hero"><p class="gq-eyebrow gq-eyebrow--mint">' + eyebrow + '</p>' +
        '<h1 class="gq-h1' + (d.name.length > 28 ? ' gq-h1--long' : '') + '" data-focus tabindex="-1">' + esc(d.name) + '</h1>' +
        (d.problemLine ? '<p class="gq-problem">' + esc(d.problemLine) + '</p>' : '') +
        (res.mode === 'combined' && res.subDiagnoses ? '<p class="gq-chips"><span class="gq-chip">TEMPO · ' + esc(res.subDiagnoses.tempo.short) + '</span><span class="gq-chip">CONNECTION · ' + esc(res.subDiagnoses.connection.short) + '</span></p>' : '') +
        '<button type="button" class="gq-strength" data-act="strength" aria-expanded="false" aria-controls="gq-sn"><span class="gq-sdot gq-sdot--' + res.strength + '"></span>' + esc(sn.label || '') + '</button>' +
        '<p id="gq-sn" class="gq-sn" hidden>' + esc(sn.text || '') + '</p>' +
        '<p class="gq-summary">' + esc(d.summary) + '</p>' + (cont ? '<p class="gq-cont">' + esc(cont) + '</p>' : '') +
        '<button type="button" class="gq-link gq-link--ondark" data-scroll="gq-tool">See your training tool ↓</button></section>';
      // R4
      var ce = counterFor(cfg, res);
      h += '<section class="gq-sec gq-because"><h2 class="gq-h2">BECAUSE YOU SAID…</h2>' + res.evidence.filter(function (e) { return !ce || e.question !== ce.question; }).slice(0, 3).map(function (e) {
        var q = byId[e.question], o = findOpt(q, e.option), sig = signalOf(e.question, o);
        return '<div class="gq-ev"><p class="gq-ev__tag">Q' + posOf(e.question) + ' · ' + esc(String(q.eyebrow).toUpperCase()) + '</p><p class="gq-quote">“' + esc(labelFor(cfg, e.question, e.option).label) + '”</p>' +
          '<p class="gq-ev__t">' + esc(e.text) + '</p>' + (sig ? '<span class="gq-sig">' + sig + '</span>' : '') + '</div>';
      }).join('');
      if (ce) {
        h += '<div class="gq-ev gq-ev--counter"><p class="gq-ev__tag">ONE ANSWER POINTED THE OTHER WAY</p><p><b>“' + esc(labelFor(cfg, ce.question, ce.option).label) + '”</b> (Q' + posOf(ce.question) + ') · ' + esc(ce.text) + '</p><p class="gq-fine">' +
          (res.mode === 'starter' ? 'That’s why this is a starting focus, not a firm call.' : 'That’s why we’ve kept this measured.') + '</p></div>';
      }
      if (res.secondary[0]) h += '<p class="gq-also">ALSO NOTED · ' + esc(res.secondary[0].name) + ': ' + esc(res.secondary[0].text) + '</p>';
      h += '<details class="gq-details"><summary>See all 10 of my answers</summary><ol class="gq-answers">' + state.order.map(function (qid, i) {
        return '<li><span>Q' + (i + 1) + ' · ' + esc(byId[qid].eyebrow) + ': <b>' + esc(labelFor(cfg, qid, res.answers[qid]).label) + '</b></span>' +
          (mine ? '<a href="#/q/' + (i + 1) + '/r" data-change="' + qid + '">Change</a>' : '') + '</li>';
      }).join('') + '</ol></details></section>';
      // R5
      function meter(label, lv, focus) {
        var n = lv === 'high' ? 3 : lv === 'moderate' ? 2 : 1;
        return '<div class="gq-meter"><div class="gq-meter__top"><span>' + label + '</span>' + (focus ? '<span class="gq-tag">YOUR PRIORITY</span>' : '') + '<b>' + esc(content.levelWords[lv]) + '</b></div>' +
          '<div class="gq-meter__bar" aria-hidden="true">' + [1, 2, 3].map(function (i) { return '<span' + (i <= n ? ' class="is-on" style="--i:' + i + '"' : '') + '></span>'; }).join('') + '</div></div>';
      }
      h += '<section class="gq-sec gq-point"><h2 class="gq-h2">WHERE YOUR ANSWERS POINT</h2>' +
        meter('Tempo &amp; rhythm', res.levels.T, res.family === 'tempo' || res.family === 'combined') +
        meter('Arm–body connection', res.levels.C, res.family === 'connection' || res.family === 'combined') +
        '<p class="gq-fine">Indicators from what you told us, not measurements of your swing.</p></section>';
      // R6
      h += '<section class="gq-sec gq-tool" id="gq-tool">' + toolInner(res) + '</section>';
      // R7
      var mean = d.meaning || [];
      h += '<section class="gq-sec"><h2 class="gq-h2">WHAT’S GOING ON</h2><p>' + esc(mean[0] || '') + '</p>' +
        (mean.length > 1 ? '<details class="gq-details gq-details--plain"><summary>Read more</summary>' + mean.slice(1).map(function (p) { return '<p>' + esc(p) + '</p>'; }).join('') + '</details>' : '') +
        '<p class="gq-fine">Based on what you told us, not video of your swing. A coach watching you swing may see things this can’t.</p></section>';
      // R8
      var adv = res.advice.map(function (id) { return content.advice[id]; }).filter(Boolean);
      h += '<section class="gq-sec gq-drill"><h2 class="gq-h2">TRY THIS TODAY (FREE, NO PURCHASE NEEDED)</h2><div class="gq-card">' + drill(adv[0]) + '</div>' +
        (adv.length > 1 ? '<details class="gq-details"><summary>One more drill for you</summary>' + adv.slice(1).map(function (a) { return '<div class="gq-card">' + drill(a) + '</div>'; }).join('') + '</details>' : '') + '</section>';
      // R9
      var pr = content.protocols[res.protocol];
      if (pr) {
        h += '<section class="gq-sec"><h2 class="gq-h2">YOUR PRACTICE ROUTINE</h2><h3 class="gq-h3">' + esc(pr.title) + '</h3><p class="gq-fine">' + esc(pr.duration || '') + '</p>' +
          '<ol>' + (pr.steps || []).map(function (s) { return '<li>' + esc(s.t || s) + '</li>'; }).join('') + '</ol>' +
          '<details class="gq-details"><summary>See the full routine</summary>' + (pr.setup ? '<p>' + esc(pr.setup) + '</p>' : '') + list(pr.steps, true) +
          (pr.feel ? '<p class="gq-eyebrow">THE FEELING TO LOOK FOR</p>' + list(pr.feel) : '') +
          (pr.mistakes ? '<p class="gq-eyebrow">COMMON MISTAKES</p>' + list(pr.mistakes) : '') +
          (pr.progress ? '<p class="gq-eyebrow">HOW TO PROGRESS</p>' + list(pr.progress) : '') + '</details></section>';
      }
      // R10
      h += '<section class="gq-sec gq-cancant"><h2 class="gq-h2">WHAT THIS TEST CAN AND CAN’T TELL YOU</h2><div class="gq-cols">' +
        '<div><p class="gq-eyebrow">CAN</p><ul><li>Spot patterns in how you describe your swing</li><li>Show which answers led here</li><li>Give you a practice priority and a drill</li></ul></div>' +
        '<div><p class="gq-eyebrow">CAN’T</p><ul><li>See your swing</li><li>Measure speed or tempo</li><li>Promise a lower score</li></ul></div></div>' +
        '<p class="gq-fine">How it works: 10 answers → 5 swing signals → fixed rules written by Dr.Golf. Same answers, same result.</p></section>';
      // R11
      if (creditOn) {
        h += '<section class="gq-sec"><div class="gq-credit" id="gq-credit"><h2 class="gq-h3" tabindex="-1">YOUR ' + esc(AMT) + ' STORE CREDIT</h2>' +
          '<div class="gq-code"><span class="gq-code__c">' + esc(credit.code) + '</span><button type="button" class="gq-btn gq-btn--sm gq-btn--ondark" data-act="copy">Copy</button></div>' +
          '<p class="gq-toast" role="status"></p><ul class="gq-terms"><li>' + esc(AMT) + ' off orders of ' + esc(MIN) + ' or more, before shipping and tax</li><li>One per customer</li>' +
          '<li>Applied automatically when you continue from this page, as a ' + esc(AMT) + ' discount at checkout</li></ul>' +
          '<p class="gq-fine">No countdown. Your result is saved on this device for 14 days.</p></div></section>';
      }
      // R12
      var links = [['shipping', 'Shipping policy'], ['refund', 'Refund policy'], ['privacy', 'Privacy']].filter(function (l) { return pol[l[0]]; })
        .map(function (l) { return '<a href="' + esc(pol[l[0]]) + '">' + l[1] + '</a>'; }).join(' · ');
      h += '<footer class="gq-sec gq-small"><p>A practice priority based on your answers, not a confirmed mechanical diagnosis or medical advice.</p>' + (links ? '<p>' + links + '</p>' : '') +
        '<p class="gq-center"><button type="button" class="gq-link" data-act="retake">Retake the swing test</button></p></footer>';
      h += '</div><div class="gq-sticky" id="gq-sticky">' + stickyInner(res) + '</div>';
      mount('result', 12, 'Your result', h);
      bindResult(str);
    }

    var stickyOff = [], toolSeen = {};
    function observe(els, opts, fn, bag) {
      els = els.filter(Boolean);
      if (!win.IntersectionObserver) { els.forEach(function (el) { fn({ target: el, isIntersecting: true, intersectionRatio: 1, boundingClientRect: { top: 1 } }); }); return; }
      var io = new win.IntersectionObserver(function (es) { es.forEach(fn); }, opts);
      els.forEach(function (el) { io.observe(el); });
      (bag || cleanups).push(function () { io.disconnect(); });
    }
    function bindResult(str) {
      var strip = app.querySelector('.gq-strip');
      if (strip) later(function () { strip.classList.add('is-in'); trackOnce('credit:' + str, 'credit_revealed', {}); }, reduced() ? 0 : 300);
      observe([].slice.call(app.querySelectorAll('.gq-meter')), { threshold: 0.4 }, function (e) { if (e.isIntersecting) e.target.classList.add('is-in'); });
      var sk = str + ':' + (preferFor(str) || '');
      observe([app.querySelector('#gq-tool')], { threshold: [0, 0.5] }, function (e) {
        if (!toolSeen[sk] && e.intersectionRatio >= 0.5) { toolSeen[sk] = 1; track('prescription_viewed', { diagnosis: curRes.diagnosis.id, family: curRes.family }); }
      });
      bindSticky();
    }
    function bindSticky() {
      stickyOff.forEach(function (f) { f(); }); stickyOff = [];
      var sticky = app.querySelector('#gq-sticky');
      if (!sticky || !sticky.innerHTML) return;
      var vis = {}, heroOut = false;
      function upd() {
        var block = Object.keys(vis).some(function (k) { return vis[k]; });
        sticky.classList.toggle('is-shown', heroOut && !block);
      }
      observe([app.querySelector('#gq-hero')], { threshold: 0 }, function (e) { heroOut = !e.isIntersecting && e.boundingClientRect.top < 0; upd(); }, stickyOff);
      var zones = [].slice.call(app.querySelectorAll('.gq-ctazone')).concat([app.querySelector('#gq-credit')]).filter(Boolean);
      zones.forEach(function (z, i) { z.setAttribute('data-z', i); });
      observe(zones, { threshold: 0 }, function (e) { vis[e.target.getAttribute('data-z')] = e.isIntersecting; upd(); }, stickyOff);
    }
    function rerenderTool(focusVid) {
      var t = app.querySelector('#gq-tool'), s = app.querySelector('#gq-sticky');
      if (!t || !curRes) return;
      t.innerHTML = toolInner(curRes);
      if (s) s.innerHTML = stickyInner(curRes);
      bindSticky();
      var b = focusVid && app.querySelector('[data-vid="' + focusVid + '"]');
      if (b) b.focus();
    }
    /* Scrolls below the sticky results header (CSS scroll-margin-top) and moves focus to the target's heading. */
    function scrollToId(id, instant) {
      var el = doc.getElementById(id) || app.querySelector('.' + id);
      if (!el) return;
      el.scrollIntoView({ behavior: instant || reduced() ? 'auto' : 'smooth', block: 'start' });
      var hd = /^H[1-6]$/.test(el.tagName) ? el : el.querySelector('h2, h3');
      if (hd) {
        if (!hd.hasAttribute('tabindex')) hd.setAttribute('tabindex', '-1');
        try { hd.focus({ preventScroll: true }); } catch (e) { /* */ }
      }
    }
    function copyCode(btn) {
      var ok = function () {
        var t = app.querySelector('.gq-toast');
        if (t) { t.textContent = 'Copied'; t.classList.add('is-on'); later(function () { t.textContent = ''; t.classList.remove('is-on'); }, 1500); }
        track('credit_code_copied', {});
      };
      function fallback() {
        var ta = doc.createElement('textarea');
        ta.value = credit.code; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        doc.body.appendChild(ta); ta.select();
        try { doc.execCommand('copy'); ok(); } catch (e) { /* */ }
        ta.remove(); btn.focus();
      }
      if (win.navigator.clipboard && win.navigator.clipboard.writeText) win.navigator.clipboard.writeText(credit.code).then(ok, fallback);
      else fallback();
    }

    /* ---------- delegated events ---------- */
    app.addEventListener('click', function (e) {
      var t = e.target.closest('[data-opt],[data-act],[data-scroll],[data-vid],[data-cta],[data-change]');
      if (!t || !app.contains(t)) return;
      if (t.hasAttribute('data-opt')) return pick(t);
      if (t.hasAttribute('data-cta')) {
        var k = t.getAttribute('data-cta');
        try {
          var ho = buildHandOff(cfg, content, curRes, k, creditOn ? credit : null);
          // A bundle reached from a single-priority result would get the PDP's "pointed to both" box: send no hand-off
          // (and clear any older one) so the bundle page shows its generic copy. The discount link still applies the credit.
          if (handOffFitsChoice(ho, k)) win.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(ho));
          else win.sessionStorage.removeItem(HANDOFF_KEY);
        } catch (err) { /* PDP shows its generic version */ }
        try {
          var hc = handOffCredit(creditOn ? credit : null);
          if (hc.creditCode) win.sessionStorage.setItem(CREDIT_KEY, JSON.stringify(hc));
          else win.sessionStorage.removeItem(CREDIT_KEY);
        } catch (err) { /* PDP shows no credit bubble */ }
        track('prescription_cta_clicked', { product: k, location: t.getAttribute('data-loc'), diagnosis: curRes.diagnosis.id, family: curRes.family, mode: curRes.mode });
        return;
      }
      if (t.hasAttribute('data-change')) { track('answer_changed_from_results', { question: t.getAttribute('data-change') }); return; }
      if (t.hasAttribute('data-scroll')) { e.preventDefault(); return scrollToId(t.getAttribute('data-scroll')); }
      if (t.hasAttribute('data-vid')) {
        var vid = +t.getAttribute('data-vid'), v = (P('band').variants || []).filter(function (x) { return x.id === vid; })[0];
        if (!v || !v.available) return;
        bandVid = vid;
        track('variant_selected', { product: 'band', variant: v.title });
        return rerenderTool(vid);
      }
      var a = t.getAttribute('data-act');
      if (a === 'why') { track('why_opened', { question: cur.q.id }); return openSheet('Why we ask', cur.q.why, t); }
      if (a === 'continue') { track('resume_continued', {}); return go('#/q/' + (firstUnanswered() || 1)); }
      if (a === 'seeresult') return go('#/result/' + ownString());
      if (a === 'startover') { resetAnswers(); save(); track('resume_restart', { implicit: false }); return route(); }
      if (a === 'retake') { resetAnswers(); save(); track('retake', {}); return go('#/q/1'); }
      if (a === 'halfway-next') return track('halfway_continued', {});
      if (a === 'skip') { track('scoring_skipped', { at_row: cur.rowsDone || 0 }); return redirect('#/result/' + cur.str); }
      if (a === 'switch') {
        state.prefer = curRes.alternateFamily; state.preferFor = cur.str; save();
        track('focus_switched', { to: state.prefer });
        route();
        // Keep the golfer at the training tool they were comparing, not back at the hero.
        return scrollToId('gq-tool-h', true);
      }
      if (a === 'strength') {
        var open = t.getAttribute('aria-expanded') === 'true', sn = app.querySelector('#gq-sn');
        t.setAttribute('aria-expanded', String(!open)); if (sn) sn.hidden = open;
        return;
      }
      if (a === 'copy') return copyCode(t);
    });
    app.addEventListener('keydown', function (e) {
      var g = e.target.closest('[role="radiogroup"]');
      if (!g || ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].indexOf(e.key) === -1) return;
      var items = [].slice.call(g.querySelectorAll('[role="radio"]:not([disabled])')), i = items.indexOf(e.target);
      if (i === -1) return;
      e.preventDefault();
      var nx = items[(i + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length];
      items.forEach(function (b) { b.tabIndex = b === nx ? 0 : -1; });
      nx.focus();
    });
    doc.addEventListener('keydown', function (e) {
      if (sheet || !cur.q || app.getAttribute('data-route') !== 'q' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || '')) return;
      var k = parseInt(e.key, 10);
      if (k >= 1 && k <= 5) { var b = app.querySelectorAll('.gq-opt')[k - 1]; if (b) { e.preventDefault(); pick(b); } }
    });
    win.addEventListener('hashchange', route);
    route();
  }

  return {
    DEFAULT_ORDER: DEFAULT_ORDER, ANGLE_Q: ANGLE_Q, HOOKS: HOOKS, DEFAULT_HOOK: DEFAULT_HOOK, LABELS: LABELS, ICONS: ICONS,
    normAngle: normAngle, buildOrder: buildOrder, validOrder: validOrder, labelFor: labelFor, tally: tally, leanOf: leanOf, noteFor: noteFor,
    creditApplies: creditApplies, withCredit: withCredit, separateSaving: separateSaving, bundleVariantFor: bundleVariantFor,
    creditFor: creditFor, formatMoney: formatMoney, formatWhole: formatWhole, handOffCredit: handOffCredit,
    buildHandOff: buildHandOff, discountHref: discountHref, ctaTargets: ctaTargets, whyText: whyText, strongestAnswer: strongestAnswer,
    counterFor: counterFor, patternValue: patternValue, continuityLine: continuityLine, handOffFitsChoice: handOffFitsChoice,
    boot: boot
  };
});
